/**
 * Write a value as block-style YAML.
 *
 * Strings are left plain only when every YAML reader (1.1 and 1.2) would read them back
 * as the same string: `yes`, `no`, `on`, `off`, `null`, numbers, dates and anything with
 * a leading indicator or an embedded `: ` or ` #` get quotes. Multi-line strings use a
 * literal block (`|`, `|-` or `|+`) so line breaks stay readable.
 */

import {
  type DataMap,
  type DataValue,
  RawNumber,
} from "@/lib/tool-kit/latex-dev/data-model";
import {
  doubleQuote,
  hasSpecialCharacter,
  isPlainSafe,
} from "@/lib/tool-kit/latex-dev/yaml-scalar";

export interface YamlEmitOptions {
  /** Spaces per nesting level: 2 or 4. */
  indent: 2 | 4;
  /** Use `|` blocks for strings with line breaks. */
  blockScalars: boolean;
}

export const DEFAULT_EMIT_OPTIONS: YamlEmitOptions = {
  indent: 2,
  blockScalars: true,
};

interface Scalar {
  /** The text after "key: " or "- ". */
  inline: string;
  /** Lines that follow (the body of a block scalar), already indented. */
  body: string[];
}

const RE_ONLY_BLANK = /^[ \t\n]*$/;
const RE_TRAILING_BREAKS = /\n+$/;

function isEmptyCollection(value: DataValue): boolean {
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return value instanceof Map && value.size === 0;
}

function isCollection(value: DataValue): boolean {
  return Array.isArray(value) || value instanceof Map;
}

/** The literal-block header and lines for a multi-line string, or null when quoting is safer. */
function blockScalar(text: string): { header: string; lines: string[] } | null {
  if (
    hasSpecialCharacter(text, true) ||
    text.includes("\r") ||
    RE_ONLY_BLANK.test(text)
  ) {
    return null;
  }
  const parts = text.split("\n");
  const firstIndex = parts.findIndex((line) => line.trim() !== "");
  // A first content line that starts with a space would need an indentation indicator,
  // and a space-only line ahead of it can be mistaken for the indentation: quote instead.
  if (
    parts[firstIndex].startsWith(" ") ||
    parts.slice(0, firstIndex).some((line) => line !== "")
  ) {
    return null;
  }
  if (!text.endsWith("\n")) {
    return { header: "|-", lines: parts };
  }
  const breaks = text.length - text.replace(RE_TRAILING_BREAKS, "").length;
  return { header: breaks === 1 ? "|" : "|+", lines: parts.slice(0, -1) };
}

function scalar(
  value: DataValue,
  level: number,
  step: number,
  useBlocks: boolean
): Scalar {
  if (value === null) {
    return { inline: "null", body: [] };
  }
  if (typeof value === "boolean") {
    return { inline: String(value), body: [] };
  }
  if (typeof value === "number") {
    return {
      inline: Number.isFinite(value) ? String(value) : "null",
      body: [],
    };
  }
  if (typeof value === "bigint") {
    return { inline: value.toString(), body: [] };
  }
  if (value instanceof RawNumber) {
    return { inline: value.text, body: [] };
  }
  const text = value as string;
  if (isPlainSafe(text)) {
    return { inline: text, body: [] };
  }
  const block = useBlocks && text.includes("\n") ? blockScalar(text) : null;
  if (block === null) {
    return { inline: doubleQuote(text), body: [] };
  }
  const pad = " ".repeat(level + step);
  return {
    inline: block.header,
    body: block.lines.map((line) => (line === "" ? "" : pad + line)),
  };
}

function keyText(key: string): string {
  return isPlainSafe(key) ? key : doubleQuote(key);
}

class Emitter {
  private readonly step: number;
  private readonly blocks: boolean;

  constructor(options: YamlEmitOptions) {
    this.step = options.indent;
    this.blocks = options.blockScalars;
  }

  node(value: DataValue, level: number): string[] {
    if (Array.isArray(value)) {
      return this.sequence(value, level);
    }
    if (value instanceof Map) {
      return this.mapping(value, level);
    }
    return [scalar(value, level, this.step, false).inline];
  }

  private mapping(map: DataMap, level: number): string[] {
    const pad = " ".repeat(level);
    const lines: string[] = [];
    for (const [key, value] of map) {
      const head = `${pad}${keyText(key)}:`;
      if (isCollection(value) && !isEmptyCollection(value)) {
        lines.push(head, ...this.node(value, level + this.step));
      } else if (isCollection(value)) {
        lines.push(`${head} ${Array.isArray(value) ? "[]" : "{}"}`);
      } else {
        const s = scalar(value, level, this.step, this.blocks);
        lines.push(`${head} ${s.inline}`, ...s.body);
      }
    }
    return lines;
  }

  private sequence(list: DataValue[], level: number): string[] {
    const pad = " ".repeat(level);
    const lines: string[] = [];
    for (const item of list) {
      if (isCollection(item) && !isEmptyCollection(item)) {
        const inner = this.node(item, level + 2);
        inner[0] = `${pad}- ${inner[0].slice(level + 2)}`;
        lines.push(...inner);
      } else if (isCollection(item)) {
        lines.push(`${pad}- ${Array.isArray(item) ? "[]" : "{}"}`);
      } else {
        const s = scalar(item, level, this.step, this.blocks);
        lines.push(`${pad}- ${s.inline}`, ...s.body);
      }
    }
    return lines;
  }
}

/** Write `value` as YAML, ending with a line break. */
export function emitYaml(
  value: DataValue,
  options: YamlEmitOptions = DEFAULT_EMIT_OPTIONS
): string {
  if (isCollection(value) && isEmptyCollection(value)) {
    return `${Array.isArray(value) ? "[]" : "{}"}\n`;
  }
  const lines = new Emitter(options).node(value, 0);
  return `${lines.join("\n")}\n`;
}
